import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { CTAButton } from "@/components/CTAButton";
import { Seo } from "@/components/Seo";
import { useAudit } from "@/components/AuditContext";
import { GUIDE_ARTICLES } from "@/lib/guideData";

export default function GuideArticle({ slug }) {
  const { openAudit } = useAudit();
  const article = GUIDE_ARTICLES[slug];

  if (!article) return null;

  const related = (article.related || [])
    .map((s) => ({ slug: s, article: GUIDE_ARTICLES[s] }))
    .filter((r) => r.article);

  return (
    <>
      <Seo title={article.title} description={article.metaDescription} path={`/guide/${slug}`} />

      <section className="bg-navy grain relative pt-40 pb-20 md:pt-48 md:pb-24 overflow-hidden">
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <Link to="/guide" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-amber-500 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Retour au guide
            </Link>
          </Reveal>
          <Reveal delay={0.05}>
            <span className="mt-6 block text-xs uppercase tracking-[0.25em] font-medium text-amber-500">{article.eyebrow}</span>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-5 font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white text-balance">
              {article.title}
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
              {article.shortAnswer}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8 space-y-14">
          {article.sections.map((s, i) => (
            <Reveal key={s.heading} delay={i * 0.05}>
              <div>
                <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-navy">{s.heading}</h2>
                <p className="mt-4 text-lg text-slate-600 leading-relaxed">{s.text}</p>
                {s.points && s.points.length > 0 && (
                  <ul className="mt-5 space-y-3">
                    {s.points.map((point) => (
                      <li key={point} className="flex items-start gap-3 text-lg text-slate-600 leading-relaxed">
                        <span className="mt-3 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" aria-hidden="true" />
                        <span>{point}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 py-20 md:py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-2xl bg-white border border-slate-200 p-8 md:p-10">
  <span className="text-xs uppercase tracking-[0.25em] font-medium text-amber-600">Ce que fait Umbo</span>

  <p className="mt-4 text-lg text-slate-600 leading-relaxed">
    {article.umboSolution}
  </p>

  {article.servicePath && article.serviceLabel && (
    <Link
      to={article.servicePath}
      className="mt-5 inline-flex font-medium text-navy hover:text-amber-600 transition-colors"
    >
      {article.serviceLabel}
    </Link>
  )}
</div>
          </Reveal>

          <Reveal delay={0.05}>
            <div className="mt-10">
              <h3 className="font-display text-xl font-semibold text-navy">En bref</h3>
              <ul className="mt-5 space-y-3">
                {article.summary.map((point) => (
                  <li key={point} className="flex items-start gap-3 text-slate-600 leading-relaxed">
                    <CheckCircle2 className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {article.faq && article.faq.length > 0 && (
        <section className="bg-white py-20 md:py-24">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-navy">Questions fréquentes</h2>
            </Reveal>
            <div className="mt-10 divide-y divide-slate-200 border-y border-slate-200">
              {article.faq.map((f) => (
                <div key={f.q} className="py-7">
                  <h3 className="font-display text-lg sm:text-xl font-semibold text-navy">{f.q}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="bg-slate-50 py-16 md:py-20">
          <div className="mx-auto max-w-3xl px-5 sm:px-8">
            <h2 className="font-display text-xl font-semibold text-navy">À lire aussi</h2>
            <ul className="mt-6 grid gap-3">
              {related.map((r) => (
                <li key={r.slug}>
                  <Link
                    to={`/guide/${r.slug}`}
                    className="group flex items-center justify-between gap-4 rounded-xl bg-white border border-slate-200 px-5 py-4 text-navy font-medium hover:border-amber-500 transition-colors"
                  >
                    <span>{r.article.title}</span>
                    <ArrowRight className="w-4 h-4 shrink-0 text-amber-600 transition-transform group-hover:translate-x-1" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section className="bg-navy grain relative py-24 md:py-28 overflow-hidden">
        <div className="relative z-10 mx-auto max-w-3xl px-5 sm:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-white text-balance">
              Parlons de votre situation, sans engagement.
            </h2>
          </Reveal>
          <Reveal delay={0.08}>
            <CTAButton size="lg" withArrow className="mt-10" onClick={openAudit} data-testid="guide-article-audit-cta">
              Demander un audit gratuit
            </CTAButton>
          </Reveal>
        </div>
      </section>
    </>
  );
}
